import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BackofficeService } from '../../../backoffice/services/backoffice.service';

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

@Component({
    selector: 'app-activate-account',
    templateUrl: './activate-account.component.html',
    styleUrls: ['./activate-account.component.css'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule]
})
export class ActivateAccountComponent implements OnInit {

  form!: FormGroup;
  token = '';
  loading = false;
  activated = false;
  activatedEmail = '';
  error = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private backofficeService: BackofficeService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';

    if (!this.token) {
      this.error = 'Lien d\'activation invalide. Veuillez contacter votre administrateur.';
    }

    this.form = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  private passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const password = group.get('password')?.value;
    const confirm = group.get('confirmPassword')?.value;
    if (password && confirm && password !== confirm) {
      return { passwordMismatch: true };
    }
    return null;
  }

  get passwordErrors(): string[] {
    const errors: string[] = [];
    const pw = this.form.get('password')?.value || '';
    if (pw.length > 0) {
      if (pw.length < 8) errors.push('Au moins 8 caractères');
      if (!/[A-Z]/.test(pw)) errors.push('Au moins une majuscule');
      if (!/[a-z]/.test(pw)) errors.push('Au moins une minuscule');
      if (!/[0-9]/.test(pw)) errors.push('Au moins un chiffre');
      if (!/[^A-Za-z0-9]/.test(pw)) errors.push('Au moins un caractère spécial');
    }
    return errors;
  }

  get passwordStrength(): number {
    const pw = this.form.get('password')?.value || '';
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return score;
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || !this.token) return;

    this.loading = true;
    this.error = '';

    try {
      const result = await this.backofficeService.activateEmployee(
        this.token,
        this.form.get('password')?.value
      );
      this.activated = true;
      this.activatedEmail = result.email;
    } catch (err: any) {
      console.error('[ActivateAccount] Error:', err);
      const message = err?.message || err?.details || '';
      if (message.includes('expiré') || message.includes('deadline')) {
        this.error = 'Ce lien d\'invitation a expiré. Contactez votre administrateur pour recevoir une nouvelle invitation.';
      } else if (message.includes('déjà été activé') || message.includes('already')) {
        this.error = 'Ce compte a déjà été activé. Vous pouvez vous connecter directement.';
      } else if (message.includes('invalide') || message.includes('not-found')) {
        this.error = 'Token d\'invitation invalide ou déjà utilisé.';
      } else {
        this.error = message || 'Erreur lors de l\'activation du compte.';
      }
    } finally {
      this.loading = false;
    }
  }

  goToLogin(): void {
    this.router.navigate(['/backoffice-login']);
  }
}
